import {
    type BrandReviewStatusFilter,
    type ModelDocStatusFilter,
    BrandAliasDao,
    BrandDao,
    BrandSourceDao,
    DocumentSearchDao,
    ModelDao,
    ModelDocumentCandidateDao,
    ModelDocumentDao,
    RockManualsJobDao,
    atomWithLoadMore,
} from '@rock/db-admin';
import type {
    Brand,
    BrandAlias,
    BrandAliasKey,
    BrandId,
    BrandRequestId,
    Model,
    ModelDocumentId,
    ModelId,
} from '@rock/types';
import { BRANDFETCH_SOURCE_ID, RockManualsJobIdSchema } from '@rock/types';
import { documentEntries, linkedDocumentIds, type ModelDocumentEntry } from '@rock/manuals';
import { normalizeBrandName, normalizeIdentifier, shallowEqual } from '@rock/util';
import { atom } from 'jotai';
import { atomFamily, atomWithRefresh } from 'jotai/utils';
import logger from '@/util/logger';

const log = logger('catalog.state');
const brandDao = new BrandDao();
const brandAliasDao = new BrandAliasDao();
const brandSourceDao = new BrandSourceDao();
const modelDao = new ModelDao();
const modelDocumentDao = new ModelDocumentDao();
const rockManualsJobDao = new RockManualsJobDao();
const candidateDao = new ModelDocumentCandidateDao();
const searchDao = new DocumentSearchDao();

// Brands
/** The brands list's two server-side filter dimensions: review status + a "starts with" name search. */
export type BrandsFilter = { reviewStatus: BrandReviewStatusFilter; namePrefix: string };

export const brandsState = atomWithLoadMore<Brand, BrandsFilter>(
    ({ limitAtom, filterAtom, sortAtom }) => {
        const reviewStatusAtom = atom((get) => get(filterAtom).reviewStatus);
        // Fold the typed text into `canonicalKey` space so the prefix range lines up with the stored field.
        const namePrefixAtom = atom((get) => {
            const raw = get(filterAtom).namePrefix.trim();
            return raw ? normalizeBrandName(raw).key : '';
        });
        return brandDao.brandsAtom(limitAtom, reviewStatusAtom, namePrefixAtom, sortAtom);
    },
    { initialFilter: { reviewStatus: 'live', namePrefix: '' } },
);

/** EVERY brand, unpaginated — the model pages' brand picker + brandId→name joins (the catalog is small). */
export const allBrandsState = brandDao.brandsAtom();

export const brandDetailsState = atomFamily(
    (brandId: BrandId | undefined) => brandDao.brandAtom(brandId),
    shallowEqual,
);

/** The brand's stored Brandfetch snapshot (admin-only) — the asset browser re-picks from this. */
export const brandSourceState = atomFamily(
    (brandId: BrandId | undefined) => brandSourceDao.brandSourceAtom(brandId, BRANDFETCH_SOURCE_ID),
    shallowEqual,
);

/** Every `brand-aliases` projection pointing at a brand — the resolvable keys on the Aliases tab. */
export const brandAliasesState = atomFamily(
    (brandId: BrandId | undefined) => brandAliasDao.aliasesByBrandAtom(brandId),
    shallowEqual,
);

const brandAliasDocState = atomFamily((key: BrandAliasKey) => brandAliasDao.brandAliasAtom(key));

/** Folded key → its `brand-aliases` doc, or null when the key resolves to nothing. */
export type BrandAliasLookup = ReadonlyMap<string, BrandAlias | null>;

/**
 * The `brand-aliases` projection for a set of folded keys — the lookup processing resolves names through,
 * read as one atom so a list of names loads together. Keyed by the keys joined with newlines (a folded key
 * never holds one), so the same set is the same atom.
 */
export const brandAliasLookupState = atomFamily((joinedKeys: string) =>
    atom(async (get): Promise<BrandAliasLookup> => {
        const keys = joinedKeys ? joinedKeys.split('\n') : [];
        const docs = await Promise.all(keys.map((key) => get(brandAliasDocState(key as BrandAliasKey))));
        return new Map(keys.map((key, index) => [key, docs[index] ?? null]));
    }),
);

export function joinAliasKeys(keys: Iterable<string>): string {
    return [...new Set(keys)].filter(Boolean).sort().join('\n');
}

/**
 * How many rows each of the brands list's status filters would show, keyed by filter value (server count
 * aggregations). The brands list and the dashboard both read it, so every number is exactly how many rows
 * its filter or link lands on. `all` counts junk too; `live` is everything but junk (and merged). Refreshable,
 * so each reader marks it stale as it unmounts and the next visit reads current numbers. Undefined when a
 * count fails, which readers show as a failure rather than losing the page to the error boundary.
 */
export const brandStatusCountsState = atomWithRefresh(
    async (): Promise<Record<BrandReviewStatusFilter, number> | undefined> => {
        try {
            const [all, reviewed, unreviewed, needsAttention, noName, junk] = await Promise.all([
                brandDao.countBrands(),
                brandDao.countBrandsWithReviewStatus('reviewed'),
                brandDao.countBrandsWithReviewStatus('unreviewed'),
                brandDao.countBrandsWithReviewStatus('needs-attention'),
                brandDao.countBrandsWithReviewStatus('no-name'),
                brandDao.countBrandsWithReviewStatus('junk'),
            ]);
            return {
                all,
                live: all - junk,
                reviewed,
                unreviewed,
                'needs-attention': needsAttention,
                'no-name': noName,
                junk,
            };
        } catch (err) {
            log.error('Failed to count brands by status', { err });
            return undefined;
        }
    },
);

/** Models linked to a brand (unsorted — callers sort; see modelsByBrandAtom). */
export const brandModelsState = atomFamily(
    (brandId: BrandId | undefined) => modelDao.modelsByBrandAtom(brandId),
    shallowEqual,
);

// Models
/**
 * The models list's two server-side filter dimensions: a "starts with" search over the canonical model
 * number, and the stored `docStatus` rollup. `brand-review` is the one view the main query can't serve
 * (it selects on a pending suggestion, and has to be complete) — it falls back to `all` here and reads
 * `modelsNeedingBrandReviewState` instead.
 */
export type ModelsFilter = { namePrefix: string; status: ModelDocStatusFilter | 'brand-review' };

export const modelsState = atomWithLoadMore<Model, ModelsFilter>(
    ({ limitAtom, filterAtom, sortAtom }) => {
        // Fold the typed text into `modelCanonical` space (uppercase, alphanumerics only) so the prefix
        // range lines up with the stored field.
        const namePrefixAtom = atom((get) => {
            const raw = get(filterAtom).namePrefix.trim();
            return raw ? normalizeIdentifier(raw) : '';
        });
        const docStatusAtom = atom((get): ModelDocStatusFilter => {
            const status = get(filterAtom).status;
            return status === 'all' || status === 'brand-review' ? 'all' : status;
        });
        return modelDao.modelsAtom(limitAtom, namePrefixAtom, docStatusAtom, sortAtom);
    },
    { initialFilter: { namePrefix: '', status: 'all' } },
);

/**
 * The brand review queue: models carrying an unconfirmed `brandSuggestion`. Its own server-side query, so
 * the queue is complete rather than "whatever page the models list had loaded". Capped — a queue this size
 * means something upstream is wrong, and the dashboard count (a true server aggregation) is the honest
 * total either way.
 *
 * Parameterized on `enabled` so the models list can mount the hook unconditionally (hooks must be) while
 * only paying for the Firestore listener when the review view is actually open.
 */
export const MODEL_BRAND_REVIEW_LIMIT = 200;
export const modelsNeedingBrandReviewState = atomFamily((enabled: boolean) =>
    modelDao.modelsNeedingBrandReviewAtom(enabled ? MODEL_BRAND_REVIEW_LIMIT : 0),
);

export const modelState = atomFamily((modelId: ModelId | undefined) => modelDao.modelAtom(modelId), shallowEqual);

/** The models waiting on a brand request: what resolving it links, or merges into a same-number twin. */
export const modelsAwaitingBrandRequestState = atomFamily(
    (requestId: BrandRequestId | undefined) => modelDao.modelsAwaitingBrandRequestAtom(requestId),
    shallowEqual,
);

/** Every model waiting on a brand request: the Brand Requests list counts each open request's from it. */
export const modelsAwaitingAnyBrandRequestState = modelDao.modelsAwaitingAnyBrandRequestAtom();

/** One live `documents/{id}` artifact — shared across models, so the family is keyed by the bare doc id. */
export const modelDocumentState = atomFamily(
    (docId: ModelDocumentId | undefined) => modelDocumentDao.documentAtom(docId),
    shallowEqual,
);

/**
 * The whole `documents/{id}` library, newest-touched first — the Documents list page. Capped; the page
 * says so when it fills, rather than pretending the tail doesn't exist.
 */
export const DOCUMENT_LIBRARY_LIMIT = 200;
export const modelDocumentLibraryState = modelDocumentDao.documentsAtom(DOCUMENT_LIBRARY_LIMIT);

/** The models linking one document, live — who a shared document reaches, as syncs link and unlink it. */
export const modelsLinkingDocumentState = atomFamily(
    (docId: ModelDocumentId) => modelDao.modelsLinkingDocumentAtom(docId),
    shallowEqual,
);

/** The documents marked as replaced by one document — its earlier revisions, live. */
export const documentsReplacedByState = atomFamily(
    (docId: ModelDocumentId) => modelDocumentDao.documentsReplacedByAtom(docId),
    shallowEqual,
);

/**
 * The model's RESOLVED document entries: its links joined to the live `documents/{id}` artifacts
 * (per-doc subscriptions — shared docs dedupe through `modelDocumentState`). Dangling links (deleted /
 * still-propagating artifacts) degrade to absent, matching `documentEntries`.
 */
export const modelDocumentEntriesState = atomFamily(
    (modelId: ModelId | undefined) =>
        atom(async (get): Promise<ModelDocumentEntry[]> => {
            const model = await get(modelState(modelId));
            const ids = linkedDocumentIds(model);
            const docs = await Promise.all(ids.map((id) => get(modelDocumentState(id))));
            return documentEntries(model, docs);
        }),
    shallowEqual,
);

/** The model's manual-acquisition job (job id == model id) — worker progress for the acquisition card. */
export const modelManualsJobState = atomFamily(
    (modelId: ModelId | undefined) =>
        rockManualsJobDao.rockManualsJobAtom(modelId ? RockManualsJobIdSchema.parse(modelId) : undefined),
    shallowEqual,
);

/**
 * How many rows each of the models list's status filters would show, keyed by filter value (server count
 * aggregations). Read by the models list and the dashboard, like `brandStatusCountsState`, and refreshable
 * and failure-tolerant for the same reasons. `all` drops archived models by `archivedAt`, the same test the
 * unfiltered list applies.
 */
export const modelStatusCountsState = atomWithRefresh(
    async (): Promise<Record<ModelsFilter['status'], number> | undefined> => {
        try {
            return await countModelsByStatus();
        } catch (err) {
            log.error('Failed to count models by status', { err });
            return undefined;
        }
    },
);

async function countModelsByStatus(): Promise<Record<ModelsFilter['status'], number>> {
    const [
        total,
        archivedAt,
        brandReview,
        missing,
        searching,
        needsReview,
        published,
        libraryOnly,
        searchFailed,
        ingestFailed,
        archived,
        parked,
        revisit,
    ] = await Promise.all([
        modelDao.countModels(),
        modelDao.countArchivedModels(),
        modelDao.countModelsNeedingBrandReview(),
        modelDao.countModelsWithDocStatus('missing'),
        modelDao.countModelsWithDocStatus('searching'),
        modelDao.countModelsWithDocStatus('needs-review'),
        modelDao.countModelsWithDocStatus('published'),
        modelDao.countModelsWithDocStatus('library-only'),
        modelDao.countModelsWithDocStatus('search-failed'),
        modelDao.countModelsWithDocStatus('ingest-failed'),
        modelDao.countModelsWithDocStatus('archived'),
        modelDao.countModelsWithDocStatus('parked'),
        modelDao.countModelsWithDocStatus('revisit'),
    ]);
    return {
        all: total - archivedAt,
        'brand-review': brandReview,
        missing,
        searching,
        'needs-review': needsReview,
        published,
        'library-only': libraryOnly,
        'search-failed': searchFailed,
        'ingest-failed': ingestFailed,
        archived,
        parked,
        revisit,
    };
}

/** Every fetched-and-verified candidate for a model — the review queue on the model page. */
export const modelDocumentCandidatesState = atomFamily(
    (modelId: ModelId | undefined) => candidateDao.candidatesAtom(modelId),
    shallowEqual,
);

/** The model's search requests, newest first (first entry = the active/latest search). */
export const modelDocumentSearchesState = atomFamily(
    (modelId: ModelId | undefined) => searchDao.searchesAtom(modelId),
    shallowEqual,
);
